import { useLoaderData } from 'react-router';
import type { loader as homeLoader } from '~/routes/_app._index';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
} from '@rocco/components/carousel';
import { useCarousel } from '@rocco/components/carousel/carousel';
import { RoccoLink } from '@rocco/components/link';
import { DiscoverHeader } from '../components/DiscoverHeader';
import { CarouselPaginationPill } from '../components/CarouselPaginationPill';
import { CarouselBrowseMoreTile } from '../components/CarouselBrowseMoreTile';
import { ScrollReveal } from '../components/ScrollReveal';

/**
 * Discover Projects (Figma `997:38861`, black section).
 *
 * Shared editorial header above a horizontally-scrolling carousel of live
 * featured projects (`projectSection.tiles`) with a pagination pill.
 */
const ProjectsPagination = () => {
    const {
        api,
        currentSlide,
        totalSlides,
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
    } = useCarousel();

    // Hide pagination when every slide already fits (nothing to scroll).
    if (!canScrollPrev && !canScrollNext) return null;

    return (
        <div className="flex justify-center max-md:pt-8 md:pt-[2.5vw] lg:pt-[3rem]">
            <CarouselPaginationPill
                total={totalSlides}
                activeIndex={currentSlide}
                onPrev={scrollPrev}
                onNext={scrollNext}
                onSelect={(index) => api?.scrollTo(index)}
                label="Projects carousel"
                theme="dark"
            />
        </div>
    );
};

interface DiscoverProjectsSectionProps {
    /** Override the eyebrow (e.g. logged-in "Recently viewed"). */
    eyebrow?: string;
    /** Override the display heading (e.g. logged-in "Recently viewed Projects"). */
    heading?: string;
}

export const DiscoverProjectsSection = ({
    eyebrow = '(01 – Projects)',
    heading = 'Discover Projects',
}: DiscoverProjectsSectionProps = {}) => {
    const { projectSection, projectPopularCategories } =
        useLoaderData<typeof homeLoader>();
    // Drop tiles without a banner image — an empty grey card breaks the row.
    const tiles = (projectSection?.tiles ?? []).filter(
        (tile) => tile.model.images?.[0]?.src,
    );

    return (
        <section className="w-full overflow-hidden bg-black text-white focus-ring-on-dark">
            <DiscoverHeader
                theme="dark"
                eyebrow={eyebrow}
                heading={heading}
                categories={projectPopularCategories}
                description="Explore completed homes, find the designs that inspire you, and connect directly with the architects who can turn your vision into reality."
                ctaLabel="View all Projects"
                ctaTo="/projects"
            />

            <ScrollReveal
                delay={120}
                className="max-md:px-6 max-md:pt-8 max-md:pb-8 md:px-[1.927vw] md:pt-[8.33vw] md:pb-[8.33vw] lg:px-[max(2.3124rem,calc((100%_-_120rem)/2_+_2.3124rem))] lg:pt-[9.996rem] lg:pb-[9.996rem]"
            >
                <Carousel
                    autoPlay={{ delay: 4000 }}
                    options={{
                        align: 'start',
                        dragFree: true,
                        duration: 35,
                        containScroll: 'trimSnaps',
                        // Wrap around at either end. Embla falls back to
                        // non-loop automatically when slides don't fill the view.
                        loop: true,
                    }}
                >
                    {/* Loop-safe spacing: flex gap doesn't survive the loop
                        wrap-around, so the gap lives on each slide instead. */}
                    <CarouselContent>
                        {tiles.map((tile) => (
                            <CarouselItem
                                key={tile.model.id}
                                className="max-md:basis-[calc(22.77rem+1rem)] max-md:px-2 md:basis-[84.13vw] md:px-[1.615vw] lg:basis-[100.956rem] lg:px-[1.938rem]"
                            >
                                <RoccoLink
                                    to={tile.model.link}
                                    className="block w-full"
                                >
                                    <div
                                        className="bg-neutral-800 relative w-full overflow-hidden rounded-tile"
                                        style={{ aspectRatio: '1553 / 868' }}
                                    >
                                        <img
                                            src={tile.model.images?.[0]?.src}
                                            alt={
                                                tile.model.images?.[0]?.alt ??
                                                tile.model.title
                                            }
                                            className="absolute inset-0 size-full max-w-none object-cover"
                                            loading="lazy"
                                        />
                                    </div>
                                </RoccoLink>
                            </CarouselItem>
                        ))}
                        {tiles.length > 0 && (
                            <CarouselBrowseMoreTile
                                to="/projects"
                                noun="Projects"
                                theme="dark"
                                itemClassName="max-md:basis-[calc(22.77rem+1rem)] max-md:px-2 md:basis-[84.13vw] lg:basis-[100.956rem] md:px-[1.615vw] lg:px-[1.938rem]"
                                boxClassName="bg-neutral-800"
                                boxStyle={{ aspectRatio: '1553 / 868' }}
                            />
                        )}
                    </CarouselContent>
                    <ProjectsPagination />
                </Carousel>
            </ScrollReveal>
        </section>
    );
};
